import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '../Modal';
import PhotoCropper from '../PhotoCropper/PhotoCropper';
import { useDialogs } from '../DialogProvider';
import apiClient from '../../services/api';
import { CalendarPost, SocialAccount } from '../../types/api';
import AccountChoices, { availableAccounts } from './AccountChoices';
import RetryDestinations from './RetryDestinations';
import { dateLabel, postingInstant, pstTime } from './calendarDates';

export function needsAttention(post: CalendarPost): boolean {
  return (!!post.posted_at && post.status !== 'scheduled' && (post.remaining_account_ids?.length || 0) > 0) || !!post.delivery_warning || post.status === 'failed' || post.schedule_state === 'failed' ||
    (post.status === 'scheduled' && post.social_accounts.length === 0);
}
export function deliveryLabel(post: CalendarPost): string {
  if (needsAttention(post)) return 'Needs attention';
  if (post.schedule_state === 'pending' && post.status === 'scheduled') return 'Scheduled';
  if (post.status === 'posted') return 'Posted';
  if (post.schedule_state === 'processing') return 'Posting';
  if (post.schedule_state === 'queued') return 'Queued';
  if (post.status === 'scheduled') return 'Scheduled';
  return 'Draft';
}

export default function DayEditor({ day, post, accounts, onClose, onSaved, onMove }: {
  day: string;
  post?: CalendarPost;
  accounts: SocialAccount[];
  onClose: () => void;
  onSaved: (post: CalendarPost | null, notice?: string) => void;
  onMove: () => void;
}) {
  const { confirm } = useDialogs();
  const [collaborators, setCollaborators] = useState((post?.platform_options?.instagram?.collaborator_usernames || []).map((name) => `@${name}`).join(', '));
  const [caption, setCaption] = useState(post?.description || '');
  const [selected, setSelected] = useState(post?.social_accounts || []);
  const [postTime, setPostTime] = useState(post?.scheduled_at ? pstTime(post.scheduled_at) : '11:00');
  const [cropSource, setCropSource] = useState<File | null>(null);
  const [originalPhoto, setOriginalPhoto] = useState<File | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const locked = !!post?.posted_at || post?.schedule_state === 'processing' || post?.schedule_state === 'sent';
  const inFlight = !!post && ['pending', 'queued', 'processing'].includes(post.schedule_state || '');
  const existing = post?.media[0];
  const source = preview || existing?.file;
  const kind = file ? file.type.split('/')[0] : existing?.media_type;
  const passed = postingInstant(day, postTime) <= Date.now();
  const activeIds = new Set(availableAccounts(accounts).map((a) => a.id));
  const unavailable = selected.some((id) => !activeIds.has(id));

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function choose(files: FileList | null) {
    if (!files?.length) return;
    if (files.length !== 1 || !/^(image|video)\//.test(files[0].type)) {
      setError('Choose one photo or video for this day.'); return;
    }
    setError(null);
    if (files[0].type.startsWith('image/')) setCropSource(files[0]);
    else { setFile(files[0]); setOriginalPhoto(null); }
  }
  async function save(publishNow = false) {
    if (busy || locked) return;
    setBusy(true); setError(null);
    const form = new FormData();
    form.append('day', day); form.append('post_time', postTime);
    form.append('description', caption);
    if (publishNow) form.append('publish_now', 'true');
    form.append('instagram_collaborators', collaborators);
    selected.forEach((id) => form.append('account_ids', String(id)));
    if (file) form.append('file', file);
    try {
      const saved = await apiClient.uploadFile<CalendarPost>('/posts/calendar/day/', form);
      if (publishNow) onSaved(saved, 'Post queued for immediate delivery.');
      else onSaved(saved);
    } catch (err) { setError((err as Error).message || 'Could not schedule. Your changes are still here.'); }
    finally { setBusy(false); }
  }
  async function remove() {
    if (!(await confirm('Remove this day’s post from the schedule?'))) return;
    setBusy(true); setError(null);
    try {
      const removed = await apiClient.delete(`/posts/calendar/day/?day=${day}`);
      if (!removed) throw new Error('Could not remove this post. It may already be publishing.');
      onSaved(null);
    } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  }
  const close = () => { if (!busy) onClose(); };
  if (cropSource) return <PhotoCropper file={cropSource} onCancel={() => setCropSource(null)} onCrop={(cropped) => {
    setOriginalPhoto(cropSource); setFile(cropped); setCropSource(null);
  }} />;
  return <Modal title={dateLabel(day)} className="modal--calendar" open onClose={close} footer={<>
    {post && post.schedule_state !== 'processing' && <button className="btn btn--ghost" disabled={busy} onClick={onMove}>Move / swap day</button>}
    {post && !locked && <button className="btn btn--danger" disabled={busy} onClick={remove}>Remove</button>}
    <button className="btn btn--ghost" onClick={close} disabled={busy}>{locked ? 'Close' : 'Cancel'}</button>
    {!locked && <button className="btn btn--secondary" onClick={() => save(true)}
      disabled={busy || !source || !selected.length || unavailable || !postTime}>Post Now</button>}
    {!locked && <button className="btn btn--primary" onClick={() => save()}
      disabled={busy || !source || !selected.length || unavailable || passed || !postTime}>
      {busy ? 'Saving…' : post ? 'Save changes' : 'Schedule post'}
    </button>}
  </>}>
    <div className="calendar-editor">
      {error && <div role="alert" className="banner banner--error">{error}</div>}
      {post && (needsAttention(post) ? <details aria-label="Delivery error details" className="banner banner--warning calendar-error-details">
        <summary>Needs attention — delivery details</summary>
        <p>{post.delivery_warning || post.schedule_error || 'Some destinations have not received this post.'}</p>
        <Link to="/accounts">Manage connections</Link>
      </details> : <p className="muted">{deliveryLabel(post)}</p>)}
      <details aria-label="Post settings" className="panel calendar-post-settings">
        <summary>Post time: {postTime} PST · Post accounts: {selected.length}</summary>
        <div className="calendar-post-settings-body">
          <label className="field">Post time (PST, UTC−8)
            <input type="time" value={postTime} required disabled={busy || locked} onChange={(e) => setPostTime(e.target.value)} />
          </label>
          <fieldset className="calendar-destinations" disabled={busy || locked}>
            <legend>Post accounts</legend>
            <AccountChoices accounts={accounts} selected={selected} onChange={setSelected} disabled={busy || locked} />
          </fieldset>
          {!availableAccounts(accounts).length && <Link to="/accounts">Connect an account</Link>}
        </div>
      </details>
      {post && !inFlight && post.remaining_account_ids?.length > 0 && (needsAttention(post) || !!post.posted_at) &&
        <RetryDestinations post={post} accounts={accounts} onQueued={(updated) => onSaved(updated, 'Retry queued for selected unsent accounts.')} />}

      <div className="calendar-media-preview">
        {source && kind === 'image' && <img src={source} alt="Post preview" />}
        {source && kind === 'video' && <video src={source} controls playsInline preload="metadata" />}
      </div>
      {!locked && <>
        <input ref={inputRef} type="file" hidden accept="image/*,video/*" disabled={busy}
          aria-label="Photo or video" onChange={(e) => { choose(e.target.files); e.target.value = ''; }} />
        <button className="upload-target" disabled={busy} onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); if (!busy) choose(e.dataTransfer.files); }}>
          {source ? 'Replace photo or video' : 'Add a photo or video'}
        </button>
      </>}
      {originalPhoto && !locked && <button type="button" className="btn btn--ghost btn--sm" disabled={busy}
        onClick={() => setCropSource(originalPhoto)}>Crop photo</button>}
      <label className="field">Caption
        <textarea rows={3} value={caption} onChange={(e) => setCaption(e.target.value)} disabled={busy || locked}
          placeholder="Something to say?" />
      </label>
      {(accounts.some((a) => a.platform === 'instagram' && selected.includes(a.id)) || collaborators) && <label className="field">
        Instagram collaborators
        <input value={collaborators} onChange={(e) => setCollaborators(e.target.value)} disabled={busy || locked}
          placeholder="@username, @username" maxLength={200} autoCapitalize="none" autoCorrect="off" spellCheck={false} />
      </label>}
      {!locked && passed && <p className="banner banner--warning">
        Post time has passed. Expand Post time / Post accounts to choose a later time, move this post to another day, or choose Post Now.
      </p>}
      {!locked && (unavailable || !selected.length) && <p className="banner banner--warning">
        Expand Post time / Post accounts and choose available destinations.
      </p>}
      {locked && <Link to="/dashboard">View delivery details</Link>}
    </div>
  </Modal>;
}
